import React, { useState, useCallback } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { formatPhoneNumber } from "../../utils/formatNumber.js";
import { apiCheckPhoneNumberAndUsername } from "../../api/backend/auth.js";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator.js";
import {
  handleUserNameValueChange,
  handlePhoneInputChange,
  handleCurrentPasswordValueChange,
  handleConfirmPasswordChange,
  onCurrentPasswordValueFocused,
  onCurrentPasswordValueBlur,
  onUserNameValueFocused,
  onUserNameValueBlur,
} from "../../controller/watcherAndClearFunctions/signupSettingsClearsWatchersFunctions.js";
import { signupErrorHandler } from "../../controller/errorHandlers/signupSettingsErrorHandler.js";
import {
  validatePasswordValue,
  validateUserNameValue,
} from "../../constants/validate.js";
import ToolTip from "../tooltip/tooltip.jsx";
import {
  EyeSlashIcon,
  EyeIcon,
  CellPhoneIcon,
  PeopleIcon,
  LockIcon,
  SkitsOlympicLogoIcon,
  MegaphoneIcon,
} from "../icons/index.jsx";
import KeyLockIcon from "../keyLockIconSwitches/keyLockIconSwitches.jsx";
import {
  InsetButtonEventListener,
  ImgTopBtn,
  FieldButton,
} from "../buttons/index.jsx";
import { errorMessages } from "../../constants/errorMessages.js";
import Loader from "../loader/loader.jsx";
import BlinkingLight from "../blinkingLight/blinkingLight.jsx";
import "./signupForm.scss";

const SignupForm = ({
  onCancel,
  acctPhoneNumberValue,
  setAcctPhoneNumberValue,
  errors,
  onSignup,
  currentContextType = "signUpForm",
}) => {
  const [usernameReferralFocused, setUsernameReferralFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userNameValueError, setUserNameValueError] = useState("");
  const [phoneNumberValueError, setPhoneNumberValueError] = useState("");
  const [phoneNumberFocused, setPhoneNumberFocused] = useState(false); // Track focus state for username
  const [phoneNumberSuggestionError, setPhoneNumberSuggestionError] =
    useState("");
  const [confirmPasswordValueError, setConfirmPasswordValueError] =
    useState("");
  const [currentPasswordValueError, setCurrentPasswordValueError] =
    useState("");
  const [userNameToolTipVisible, setUserNameToolTipVisible] = useState(false);
  const [currentPasswordToolTipVisible, setCurrentPasswordToolTipVisible] =
    useState(false);
  const [suggestedUsernames, setSuggestedUsernames] = useState([]);
  const [userNameValidationRules, setUserNameValidationRules] = useState([
    false,
  ]);
  const [userNameValueFocused, setUserNameValueFocused] = useState(false);
  const [currentPasswordValueFocused, setCurrentPasswordValueFocused] =
    useState(false);
  const [showCurrentPasswordValue, setShowCurrentPasswordValue] =
    useState(false);
  const [confirmPasswordValueFocused, setConfirmPasswordValueFocused] =
    useState(false);
  const [showConfirmPasswordValue, setShowConfirmPasswordValue] =
    useState(false);
  const [passwordValidationRules, setPasswordValidationRules] = useState([
    false,
  ]);
  const [userNameValue, setUserNameValue] = useState(
    localStorage.getItem(getStorageKey("userNameValue", currentContextType)) ||
      ""
  );
  // New optional referral field following the same pattern
  const [usernameReferral, setUsernameReferral] = useState(
    localStorage.getItem(
      getStorageKey("usernameReferral", currentContextType)
    ) || ""
  );
  const [confirmPasswordValue, setConfirmPasswordValue] = useState(
    localStorage.getItem(
      getStorageKey("confirmPasswordValue", currentContextType)
    ) || ""
  );
  const [currentPasswordValue, setCurrentPasswordValue] = useState(
    localStorage.getItem(
      getStorageKey("currentPasswordValue", currentContextType)
    ) || ""
  );

  const checkPhoneNumberAndUsername = useCallback(
    async (phoneNumber, username) => {
      try {
        const phoneNumberWithPrefix = formatPhoneNumber(phoneNumber);
        const response = await apiCheckPhoneNumberAndUsername(
          phoneNumberWithPrefix,
          username
        );
        console.log("Phone number and username check response:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error checking phone number and username:", error);
        return {
          phoneExists: false,
          usernameResult: { exists: false, suggestedUsernames: [] },
        };
      }
    },
    []
  );

  const handleSignupClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1) client-side validation
      const { errors } = signupErrorHandler(
        userNameValue,
        acctPhoneNumberValue,
        confirmPasswordValue,
        currentPasswordValue,
        currentContextType,
        setUserNameValueError,
        setUserNameToolTipVisible,
        setPhoneNumberSuggestionError,
        setConfirmPasswordValueError,
        setCurrentPasswordValueError,
        setCurrentPasswordToolTipVisible
      );

      if (Object.keys(errors).length > 0) {
        console.log("Validation errors found:", errors);
        return;
      }
      console.log("Validation passed.");

      // 2) backend pre-check
      const result = await checkPhoneNumberAndUsername(
        acctPhoneNumberValue.replace(/\D/g, ""),
        userNameValue
      );
      console.log("Combined check result:", result);
      if (result.phoneExists && result.phoneSignupIncomplete) {
        // resume incomplete signup
        await onSignup({
          acctPhoneNumberValue,
          userNameValue,
          currentPasswordValue,
          currentContextType,
        });

        setLoading(false);
        return;
      } else if (!result.phoneExists && result.usernameResult.exists) {
        console.log(
          "Username exists but phone number does not. Suggesting usernames."
        );
        // username taken
        setUserNameValueError("Create a unique username");
        setSuggestedUsernames(result.usernameResult.suggestedUsernames);
        setLoading(false);
        return;
      } else if (!result.phoneExists && !result.usernameResult.exists) {
        // brand-new signup
        console.log(
          "Phone number and username are new, proceeding with signup."
        );
        await onSignup({
          acctPhoneNumberValue,
          userNameValue,
          currentPasswordValue,
          currentContextType,
        });
        return;
      } else if (result.phoneExists && !result.phoneSignupIncomplete) {
        console.log("Phone number exists and signup is complete.");
        // fully registered
        setPhoneNumberSuggestionError(
          "Account already registered with this phone number"
        );
        return;
      }
    } catch (error) {
      console.error("Error during validation and signup:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = (e) => {
    // hand your setLoading into the parent handler
    onCancel(e, setLoading);
  };

  return (
    <div id="signupForm" className="signup-modal-content slideDown">
      <div className="signup-modal-body">
        <form action="" style={{ position: "relative" }}>
          <div className="client-form">
            <div id="logo-header">
              <SkitsOlympicLogoIcon />
              <h3>Skits Olympic</h3>
            </div>
            <div className="input-field ">
              <div className="input-group blinker-wrapper">
                <div className="input-wrapper">
                  <PhoneInput
                    id="signUpMobile"
                    type="text"
                    country={"us"}
                    onlyCountries={["us"]}
                    disableDropdown={true}
                    autoComplete="off"
                    placeholder={
                      phoneNumberValueError
                        ? phoneNumberValueError
                        : phoneNumberFocused ||
                            (acctPhoneNumberValue &&
                              acctPhoneNumberValue !== "1")
                          ? ""
                          : "...Enter your U.S. mobile here"
                    }
                    value={acctPhoneNumberValue}
                    onChange={(value) =>
                      handlePhoneInputChange(
                        value,
                        setAcctPhoneNumberValue,
                        currentContextType,
                        getStorageKey,
                        phoneNumberValueError,
                        setPhoneNumberValueError
                      )
                    }
                    inputClass={`signup-control-phonelibrary-placeholder ${
                      phoneNumberValueError
                        ? "custom-required-placeholder-overide-phonelibrary-style field-background-color-error"
                        : "signup-control-phonelibrary-placeholder"
                    } ${phoneNumberFocused ? "focused" : ""}`}
                    inputStyle={{
                      width: "100%",
                      borderColor: phoneNumberValueError ? "red" : "",
                    }}
                    onFocus={() => {
                      setPhoneNumberFocused(true);
                      setPhoneNumberSuggestionError("");
                    }}
                    onBlur={() => setPhoneNumberFocused(false)}
                    error={!!phoneNumberValueError}
                    disableCountryCode={true} // Disable the country code
                    helperText={phoneNumberValueError && "Invalid phone number"}
                  />
                </div>
                {!(phoneNumberFocused || acctPhoneNumberValue) && (
                  <>
                    <CellPhoneIcon
                      fillColor="var(--color-white)"
                      style={{
                        opacity: 0.68,
                        transition: "opacity 0.2s ease-in-out",
                        position: "absolute",
                        left: "50px", // Position relative to the input field
                        top: "40%",
                        transform: "translateY(-50%)", // Center the icon vertically
                      }}
                    />
                    <span
                      className="blinking-cursor-black-color"
                      style={{
                        position: "absolute",
                        left: "77px", // Position relative to the input field
                        top: "40%",
                        transform: "translateY(-50%)", // Center the cursor vertically
                      }}
                    >
                      |
                    </span>
                  </>
                )}
                {phoneNumberSuggestionError && (
                  <div className="signup-phone-error-message">
                    {phoneNumberSuggestionError}
                  </div>
                )}
              </div>
              <div className="input-grid2">
                <div className="input-group-with-tooltip">
                  <div className="input-group blinker-wrapper">
                    <div className="input-wrapper">
                      <input
                        id="userName"
                        autoComplete="off"
                        type={userNameValue ? "text" : "userName"}
                        placeholder={
                          userNameValueError
                            ? userNameValueError
                            : "...Create a username, enter here"
                        }
                        className={`${userNameValueFocused ? "focused" : ""} ${
                          userNameValueError
                            ? "required-placeholder border-error field-background-color-error"
                            : ""
                        }`}
                        value={userNameValue}
                        onChange={(e) =>
                          handleUserNameValueChange(
                            e.target.value,
                            setUserNameValue,
                            currentContextType,
                            validateUserNameValue,
                            setUserNameValidationRules,
                            setUserNameToolTipVisible,
                            userNameValueError,
                            setUserNameValueError
                          )
                        }
                        onFocus={() =>
                          onUserNameValueFocused(
                            userNameValue,
                            validateUserNameValue,
                            setUserNameValueFocused,
                            setUserNameValidationRules,
                            setUserNameToolTipVisible
                          )
                        }
                        onBlur={() =>
                          onUserNameValueBlur(
                            setUserNameValueFocused,
                            setUserNameToolTipVisible
                          )
                        }
                      />
                      {!(userNameValueFocused || userNameValue) && (
                        <>
                          <PeopleIcon
                            className="input-icon"
                            fillColor="var(--color-white)"
                            style={{
                              opacity: 0.68,
                              transition: "opacity 0.2s ease-in-out",
                            }}
                          />
                          <span className="blinking-cursor-black-color signup-username-cursor">
                            |
                          </span>
                        </>
                      )}
                      {userNameValue && (
                        <FieldButton onClick={() => setUserNameValue("")}>
                          Clear
                        </FieldButton>
                      )}
                    </div>
                  </div>
                  {(userNameValueFocused || userNameValueError) && (
                    <ToolTip
                      rules={userNameValidationRules}
                      toolTipVisible={userNameToolTipVisible}
                      setToolTipVisible={setUserNameToolTipVisible}
                    />
                  )}
                </div>
              </div>
              {suggestedUsernames.length > 0 && (
                <div className="suggestions">
                  <p className="userNameMesssage">
                    Username is Taken Suggested Usernames Below:
                  </p>
                  <ul>
                    {suggestedUsernames.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="input-grid2">
                <div className="input-group-with-tooltip">
                  <div className="input-group blinker-wrapper">
                    <div className="input-wrapper">
                      <input
                        type={showCurrentPasswordValue ? "text" : "password"}
                        id="currentPassword"
                        placeholder={
                          currentPasswordValueError
                            ? currentPasswordValueError
                            : "...Create a password, enter here"
                        }
                        className={`${currentPasswordValueFocused ? "focused" : ""} ${
                          currentPasswordValueError
                            ? "required-placeholder border-error field-background-color-error"
                            : ""
                        }`}
                        value={currentPasswordValue}
                        onChange={(e) =>
                          handleCurrentPasswordValueChange(
                            e.target.value,
                            setCurrentPasswordValue,
                            currentContextType,
                            getStorageKey,
                            validatePasswordValue,
                            setPasswordValidationRules,
                            setCurrentPasswordToolTipVisible,
                            currentPasswordValueError,
                            setCurrentPasswordValueError
                          )
                        }
                        onFocus={() =>
                          onCurrentPasswordValueFocused(
                            currentPasswordValue,
                            validatePasswordValue,
                            setCurrentPasswordValueFocused,
                            setPasswordValidationRules,
                            setCurrentPasswordToolTipVisible
                          )
                        }
                        onBlur={() =>
                          onCurrentPasswordValueBlur(
                            setCurrentPasswordValueFocused,
                            setCurrentPasswordToolTipVisible
                          )
                        }
                      />
                    </div>
                    {!(currentPasswordValueFocused || currentPasswordValue) && (
                      <>
                        <LockIcon
                          className="lock-icon"
                          fillColor="var(--color-white)"
                          locked={!showCurrentPasswordValue}
                          style={{
                            opacity: 0.68,
                            transition: "opacity 0.2s ease-in-out",
                          }}
                        />
                        <span className="blinking-cursor-black-color signup-password-cursor">
                          |
                        </span>
                      </>
                    )}
                    {currentPasswordValue && currentPasswordValue !== "" && (
                      <div
                        className="show-hide-password-toggle"
                        onClick={(e) => {
                          setShowCurrentPasswordValue((prev) => !prev);
                        }}
                        style={{
                          position: "absolute",
                          right: "10px", // Position it on the right inside the input field
                          top: "50%",
                          transform: "translateY(-50%)", // Center it vertically
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {showCurrentPasswordValue ? (
                          <EyeSlashIcon
                            width="20"
                            height="15"
                            fillColor="var(--color-lightyellow)"
                          />
                        ) : (
                          <EyeIcon
                            width="20"
                            height="15"
                            fillColor="var(--color-lightyellow)"
                          />
                        )}
                        <span className="show-hide-label">
                          {showCurrentPasswordValue ? "Hide" : "Show"}
                        </span>
                      </div>
                    )}
                  </div>
                  {(currentPasswordValueFocused ||
                    currentPasswordValueError) && (
                    <ToolTip
                      rules={passwordValidationRules}
                      toolTipVisible={currentPasswordToolTipVisible}
                      setToolTipVisible={setCurrentPasswordToolTipVisible}
                    />
                  )}
                </div>
                <div className="input-group blinker-wrapper">
                  <div className="input-wrapper">
                    <input
                      type={showConfirmPasswordValue ? "text" : "password"} // Correct input type here
                      id="confirmPassword"
                      placeholder={
                        confirmPasswordValueError
                          ? confirmPasswordValueError
                          : "...Re-enter your password here"
                      }
                      className={`${confirmPasswordValueFocused ? "focused" : ""} ${
                        confirmPasswordValueError
                          ? "required-placeholder border-error field-background-color-error"
                          : ""
                      }`}
                      value={confirmPasswordValue}
                      onChange={(e) =>
                        handleConfirmPasswordChange(
                          e.target.value,
                          confirmPasswordValueError,
                          currentContextType,
                          setConfirmPasswordValue,
                          setConfirmPasswordValueError
                        )
                      }
                      errors={errors}
                      onFocus={() => setConfirmPasswordValueFocused(true)}
                      onBlur={() => setConfirmPasswordValueFocused(false)}
                    />
                  </div>
                  {confirmPasswordValueError &&
                    confirmPasswordValueError !==
                      errorMessages.confirmPassword && (
                      <div className="error-message">
                        {confirmPasswordValueError}
                      </div>
                    )}

                  {!(confirmPasswordValueFocused || confirmPasswordValue) && (
                    <>
                      <KeyLockIcon
                        className="lock-icon"
                        fillColor="var(--color-white)"
                        locked={!showConfirmPasswordValue}
                        style={{
                          opacity: 0.68,
                          transition: "opacity 0.2s ease-in-out",
                        }}
                      />
                      <span className="blinking-cursor-black-color signup-confirmpassword-cursor">
                        |
                      </span>
                    </>
                  )}
                  {confirmPasswordValue && confirmPasswordValue !== "" && (
                    <div
                      className="show-hide-password-toggle"
                      onClick={() =>
                        setShowConfirmPasswordValue((prev) => !prev)
                      }
                      style={{
                        position: "absolute",
                        right: "10px", // Position it on the right inside the input field
                        top: "30%",
                        transform: "translateY(-50%)", // Center it vertically
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {showConfirmPasswordValue ? (
                        <EyeSlashIcon
                          width="20"
                          height="15"
                          fillColor="var(--color-lightyellow)"
                        />
                      ) : (
                        <EyeIcon
                          width="20"
                          height="15"
                          fillColor="var(--color-lightyellow)"
                        />
                      )}
                      <span
                        className="show-hide-label"
                        style={{ marginLeft: "5px" }}
                      >
                        {showConfirmPasswordValue ? "Hide" : "Show"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="input-grid2">
                <div className="input-group">
                  <div
                    className="input-wrapper"
                    style={{ position: "relative" }}
                  >
                    <input
                      id="usernameReferral"
                      autoComplete="off"
                      type="text"
                      placeholder="Optional: Enter referral code"
                      value={usernameReferral}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUsernameReferral(value);
                        localStorage.setItem(
                          getStorageKey("usernameReferral", currentContextType),
                          value
                        );
                      }}
                      onFocus={() => setUsernameReferralFocused(true)}
                      onBlur={() => setUsernameReferralFocused(false)}
                      className="referral-input"
                    />
                    {!(usernameReferralFocused || usernameReferral) && (
                      <>
                        <MegaphoneIcon
                          className="referral-icon"
                          fillColor="var(--color-white)"
                          style={{
                            position: "absolute",
                            left: "10px", // Adjust this value as needed
                            top: "50%",
                            transform: "translateY(-50%)",
                            opacity: 0.68,
                            transition: "opacity 0.2s ease-in-out",
                          }}
                        />
                        <span
                          className="blinking-cursor-black-color signup-referral-cursor"
                          style={{
                            position: "absolute",
                            left: "40px", // Adjust to appear right of the icon
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          |
                        </span>
                      </>
                    )}
                    {usernameReferral && (
                      <FieldButton onClick={() => setUsernameReferral("")}>
                        Clear
                      </FieldButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="position-relative">
              <ImgTopBtn
                image={
                  <BlinkingLight blinkerLightStyles={{ marginRight: "10px" }} />
                }
                imgTopBtnWidth="100%"
                imgTopBtnTextFontSize="1.3rem"
                imgTopBtnFontColor="#b03a2e"
                imgTopBtnText={
                  currentContextType ? "Verify Account" : "Resume Signup"
                }
                textClassName="responsiveBtnText"
                onClick={handleSignupClick}
              />
            </div>
            {loading && <Loader className="signup-loader-position" />}
            <InsetButtonEventListener
              btnText="X Cancel"
              className="white-gold"
              textClassName="responsiveBtnText"
              style={{
                width: "150px",
                fontSize: "1.3rem",
              }}
              onClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default SignupForm;
